import type { StackProps } from '@chakra-ui/react';
import { Box, HStack, Text } from '@chakra-ui/react';
import { ChainLayer } from '@cronos-app/sdk';
import { ChainIcons } from '@ui/config/layer';
import { paths } from '@ui/config/paths';
import {
  useGlobalChainLayer,
  useIsUsingChainLayerFromQuery,
} from '@ui/hooks/useGlobalChainLayer';
import { useSwitchChainLayer } from '@ui/hooks/useSwitchNetwork';
import { useRouterWithI18n } from '@ui/locales/browserSSG';
import { useState } from 'react';

type ChainIdSelector = StackProps;
const ChainIdSelector: React.FC<ChainIdSelector> = ({ ...props }) => {
  const switchChainIdMutation = useSwitchChainLayer();
  const globalChainLayer = useGlobalChainLayer();
  const [switchingChainLayer, setSwitchChainLayer] =
    useState<ChainLayer | null>(null);
  const isUsingChainLayerFromQuery = useIsUsingChainLayerFromQuery();
  const router = useRouterWithI18n();

  const chainLayer = switchingChainLayer ?? globalChainLayer;
  const isChecked = chainLayer === ChainLayer.CronosEvm;

  const onChainLayerChange = async (chainLayer: ChainLayer) => {
    setSwitchChainLayer(chainLayer);
    try {
      await switchChainIdMutation.mutateAsync({ chainLayer });
      if (isUsingChainLayerFromQuery) await router.push(paths.agents());
    } finally {
      setSwitchChainLayer(null);
    }
  };

  const ZkChainIcon = ChainIcons[ChainLayer.L2];
  const CronosChainIcon = ChainIcons[ChainLayer.CronosEvm];

  return (
    <HStack
      minW="min(280px, 100%)"
      justifyContent="center"
      p="4px"
      rounded="full"
      bgColor="text.default"
      position="relative"
      textStyle="cta"
      cursor="pointer"
      css={{
        '& *': {
          transition: 'all 0.2s',
        },
      }}
      {...props}
    >
      <Box
        width="140px"
        position="absolute"
        rounded="100px"
        border="2px solid #65E9D1"
        background="#0DD4B0"
        top="4px"
        bottom="4px"
        left={!isChecked ? '4px' : `calc(100% - ${140 + 4}px)`}
        pointerEvents="none"
      />
      <HStack
        px={2}
        height="28px"
        width="140px"
        zIndex={isChecked ? 1 : 0}
        whiteSpace="nowrap"
        onClick={() => onChainLayerChange(ChainLayer.L2)}
        justifyContent="center"
      >
        {ZkChainIcon && <ZkChainIcon boxSize={4} />}
        <Text
          color={isChecked ? 'text.secondary' : 'background.offWhite'}
          textStyle="body3Bold"
        >
          Cronos zkEVM
        </Text>
      </HStack>
      <HStack
        whiteSpace="nowrap"
        height="28px"
        width="140px"
        px={2}
        zIndex={isChecked ? 1 : 0}
        onClick={() => onChainLayerChange(ChainLayer.CronosEvm)}
        justifyContent="center"
      >
        {CronosChainIcon && <CronosChainIcon boxSize={4} />}
        <Text
          color={isChecked ? 'background.offWhite' : 'text.secondary'}
          textStyle="body3Bold"
        >
          Cronos EVM
        </Text>
      </HStack>
    </HStack>
  );
};

export const SelectChain: React.FC<StackProps> = (props) => {
  return (
    <HStack textStyle="body2" justify="space-between" {...props}>
      <Text>Network</Text>
      <ChainIdSelector />
    </HStack>
  );
};
